import { getNativeSystemTheme, notifyNativeThemeMode } from "../NativeAppBrdige"

export type ThemeChoice = "system" | "light" | "dark"
export type ThemeMode = "light" | "dark"

const THEME_CHOICE_KEY = "rinvox-theme-choice"

// The OS / device preference (used when the choice is "system").
// Prefer the appearance reported by the native host (see getNativeSystemTheme),
// because inside a native WebView `prefers-color-scheme` follows the app's own
// uiMode rather than the device, and we lock the app to light.
export const getSystemTheme = (): ThemeMode => {
    const native = getNativeSystemTheme()
    if (native) return native
    return (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) ? "dark" : "light"
}

// The user's saved choice. Defaults to "system" when nothing was ever picked.
export const getThemeChoice = (): ThemeChoice => {
    const v = localStorage.getItem(THEME_CHOICE_KEY)
    return (v === "light" || v === "dark" || v === "system") ? v : "system"
}

// Resolve a choice into an actual light/dark mode.
export const resolveTheme = (choice: ThemeChoice = getThemeChoice()): ThemeMode =>
    choice === "system" ? getSystemTheme() : choice

// Apply an actual mode to the document (CSS + Metronic in sync).
const applyMode = (mode: ThemeMode) => {
    document.documentElement.setAttribute("data-bs-theme", mode)
    try { localStorage.setItem("data-bs-theme", mode) } catch { }
    try { window.KTThemeMode?.setMode?.(mode) } catch { }
    // Keep the native status/navigation bars in sync with the resolved mode.
    notifyNativeThemeMode(mode)
}

// Persist the user's choice and apply the resolved mode.
export const applyThemeChoice = (choice: ThemeChoice): ThemeMode => {
    try { localStorage.setItem(THEME_CHOICE_KEY, choice) } catch { }
    const mode = resolveTheme(choice)
    applyMode(mode)
    return mode
}

// Apply the stored choice at startup, and keep following the OS while the
// choice stays "system". onChange lets the React layer sync its darkMode state.
export const initTheme = (onChange?: (mode: ThemeMode) => void) => {
    applyMode(resolveTheme())

    const mql = window.matchMedia?.("(prefers-color-scheme: dark)")
    if (!mql) return

    const handler = () => {
        if (getThemeChoice() !== "system") return
        const mode = getSystemTheme()
        applyMode(mode)
        onChange?.(mode)
    }

    if (mql.addEventListener) mql.addEventListener("change", handler)
    else if ((mql as any).addListener) (mql as any).addListener(handler)

    // Re-apply the current mode and re-sync the native chrome. Fired by the
    // native host both when it first exposes the device appearance (so an
    // explicit dark choice on a light device gets its status bar recolored)
    // and whenever the device appearance changes, since
    // matchMedia("prefers-color-scheme") doesn't fire inside the WebView.
    window.addEventListener("rinvox-system-theme-changed", () => {
        const mode = resolveTheme()
        applyMode(mode)
        onChange?.(mode)
    })
}

// ---- Backward-compatible helpers (used by the header dark-mode toggle) ----
export const SetThemeToLight = () => applyThemeChoice("light")
export const SetThemeToDark = () => applyThemeChoice("dark")
export const GetThemeMode = (): string => resolveTheme()
